import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { ApiError } from '@geefamily/api';
import { describeError, useLogin, useResendVerification } from '../api';
import { TelegramLoginPanel } from './TelegramLoginPanel';
import { AuthDivider, AuthSwitch } from './AuthDialogParts';
import type { AuthMode } from '../auth-dialog-context';

const schema = z.object({
  email: z.email('Geçerli bir e-posta adresi girin.'),
  password: z.string().min(1, 'Parolanızı girin.'),
});

type FormValues = z.infer<typeof schema>;

interface LoginFormProps {
  onAuthenticated: () => void;
  onModeChange: (mode: AuthMode) => void;
}

export function LoginForm({ onAuthenticated, onModeChange }: LoginFormProps) {
  const login = useLogin();
  const resend = useResendVerification();

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  // Matched on the CODE, not the message: the wording is the server's and may be
  // reworded, but the code is contract.
  const unverified =
    login.error instanceof ApiError && login.error.code === 'identity.email.not_verified';

  // mutate, not mutateAsync. react-hook-form rethrows whatever the submit
  // handler throws, and the `void onSubmit(event)` below discards it — so every
  // wrong password would surface as an unhandled rejection, which also fails a
  // Vitest run outright. The error still renders: it comes from `isError`.
  const onSubmit = handleSubmit((values) => login.mutate(values, { onSuccess: onAuthenticated }));

  return (
    <div className="flex flex-col gap-5 pb-2">
      <TelegramLoginPanel onSignedIn={onAuthenticated} />

      <AuthDivider />

      <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4" noValidate>
        {login.isError ? (
          unverified ? (
            /*
              Its own case, because the API's message is English and, more to the
              point, telling somebody "verify your email" without a way to get
              another one is a dead end — the first mail is usually in a spam
              folder they have already given up on.

              Only reachable with the CORRECT password: the check sits after
              password verification, so this reveals nothing to anybody guessing
              addresses.
            */
            <Alert tone="info">
              <p>Giriş yapmadan önce e-posta adresini doğrulaman gerekiyor.</p>
              {resend.isSuccess ? (
                <p className="mt-2 text-emerald-300">
                  Doğrulama bağlantısını tekrar gönderdik. Gelen kutunu ve spam klasörünü kontrol
                  et.
                </p>
              ) : (
                <button
                  type="button"
                  disabled={resend.isPending}
                  onClick={() => resend.mutate(getValues('email'))}
                  className="mt-2 text-sm underline underline-offset-4 transition hover:text-surface-100 disabled:opacity-60"
                >
                  {resend.isPending ? 'Gönderiliyor…' : 'Doğrulama e-postasını tekrar gönder'}
                </button>
              )}
            </Alert>
          ) : (
            <Alert tone="error">{describeError(login.error)}</Alert>
          )
        ) : null}

        <Field
          label="E-posta"
          type="email"
          autoComplete="email"
          placeholder="ornek@eposta.com"
          error={errors.email?.message}
          {...register('email')}
        />

        <Field
          label="Parola"
          type="password"
          autoComplete="current-password"
          error={errors.password?.message}
          {...register('password')}
        />

        <Button type="submit" loading={login.isPending}>
          Giriş yap
        </Button>
      </form>

      <div className="flex flex-col gap-2 text-center text-sm">
        <button
          type="button"
          onClick={() => onModeChange('forgot')}
          className="text-surface-400 transition hover:text-surface-200"
        >
          Parolamı unuttum
        </button>

        <AuthSwitch
          question="Hesabın yok mu?"
          action="Kayıt ol"
          onClick={() => onModeChange('register')}
        />
      </div>
    </div>
  );
}
